import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  GlobalMessageService,
  GlobalMessageType,
  OccEndpointsService,
  TranslationService
} from '@spartacus/core';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { UigExportComponent } from '../../../app/components/uig-export/uig-export.component';
import { DeliveryNotesOverviewDefaultSortOptions, DeliveryNotesOverviewRequest, DeliveryNotesOverviewSortData } from './delivery-notes.models';

@Injectable({
  providedIn: 'root',
})
export class DeliveryNotesService {
  constructor(
    private modalService: NgbModal,
    private httpClient: HttpClient,
    private endpoints: OccEndpointsService,
    private translation: TranslationService,
    private messageService: GlobalMessageService,
    private datepipe: DatePipe
  ) { }

  private actualSearch: DeliveryNotesOverviewRequest = {
    deliveryId: '',
    productCode: '',
    deliveryDateFrom: this.getStartDate(),
    deliveryDateTo: this.getEndDate()
  };
  private savedSortData: DeliveryNotesOverviewSortData = {
    sortDirection: '',
    sortOption: ''
  };

  private checkInitSearchData() {
    if (this.actualSearch == null) {
      this.actualSearch = {};
      this.clearSearchData();
    }
  }

  private checkInitSortData() {
    if (this.savedSortData == null) {
      this.savedSortData = {};
      this.clearSortData();
    }
  }

  public saveSearchData(
    deliveryId: string,
    productCode: string,
    deliveryDateFrom: string,
    deliveryDateTo: string
  ) {
    this.checkInitSearchData();
    this.actualSearch.deliveryId = deliveryId;
    this.actualSearch.productCode = productCode;
    this.actualSearch.deliveryDateFrom = deliveryDateFrom;
    this.actualSearch.deliveryDateTo = deliveryDateTo;
  }

  public getSearchData(): DeliveryNotesOverviewRequest {
    this.checkInitSearchData();
    return this.actualSearch;
  }

  public getSortData(): DeliveryNotesOverviewSortData {
    this.checkInitSearchData();
    return this.savedSortData;
  }

  public clearSearchData() {
    this.checkInitSearchData();
    this.actualSearch.deliveryId = '';
    this.actualSearch.productCode = '';
    this.actualSearch.deliveryDateFrom = this.getStartDate();
    this.actualSearch.deliveryDateTo = this.getEndDate()
  }

  public clearSortData() {
    this.checkInitSortData();
    this.savedSortData.sortOption =
      DeliveryNotesOverviewDefaultSortOptions[
      DeliveryNotesOverviewDefaultSortOptions.deliveryNumber
      ];
    this.savedSortData.sortDirection = 'desc';
  }

  public getStartDate(): string {
    let startDate: Date = new Date();
    startDate.setDate(startDate.getDate() - 30);
    let result = this.datepipe.transform(startDate, 'yyyy-MM-dd');
    return result;
  }

  public getEndDate(): string {
    return this.datepipe.transform(new Date(), 'yyyy-MM-dd');
  }

  public saveSortData(sortOption: string, sortDirection: string) {
    this.checkInitSortData();
    this.savedSortData.sortOption = sortOption;
    this.savedSortData.sortDirection = sortDirection;
  }

  public printOrExportDeliveryNote(deliveryNumber: string, marketplace: boolean): void {
    this.modalService.open(UigExportComponent, { centered: true }).result.then(
      (result) => {
        if (result === 'print') {
          window.print();
        } else {
          this.exportDeliveryNote(result, deliveryNumber, marketplace);
        }
      },
      (reason) => { }
    );
  }

  public printOrExportDeliveryNotesOverview(formData: DeliveryNotesOverviewRequest, marketplace: boolean): void {
    this.modalService.open(UigExportComponent, { centered: true }).result.then(
      (result) => {
        if (result === 'print') {
          window.print();
        } else {
          this.exportDeliveryNotes(result, formData, marketplace);
        }
      },
      (reason) => { }
    );
  }

  exportDeliveryNote(exportType: string, deliveryNumber: string, marketplace: boolean) {
    let url = this.endpoints.buildUrl(
      'deliveryNotesDetailDownload',
      { urlParams: { deliveryId: deliveryNumber},  queryParams: {isMarketplace: marketplace }}
    )

    this.httpClient
      .post(url, null, { observe: 'body', responseType: 'blob' })
      .pipe(
        tap(
          (data) => {
            this.translation
              .translate('deliveryNotes.fileName.delivery')
              .pipe(take(1))
              .subscribe((fileName) => {
                var a = document.createElement('a');
                a.setAttribute('download', fileName + '.' + exportType);
                a.href = URL.createObjectURL(data);
                a.click();
              });
          },
          () => {
            this.translation
              .translate('deliveryNotes.errorExportOrder')
              .pipe(take(1))
              .subscribe((message) => {
                this.messageService.add(
                  message,
                  GlobalMessageType.MSG_TYPE_ERROR
                );
              });
          }
        )
      )
      .subscribe((res) => { });
  }

  exportDeliveryNotes(
    selectExport: string,
    formData: DeliveryNotesOverviewRequest,
    marketplace: boolean
  ) {
    this.translation
      .translate('deliveryNotes.downloadInfo')
      .pipe(take(1))
      .subscribe((message) => {
        this.messageService.add(message, GlobalMessageType.MSG_TYPE_INFO);
      });
    this.downLoadDeliveryNotesOverview(selectExport, formData, marketplace)
      .pipe(
        tap(
          (data) => {
            this.translation
              .translate('deliveryNotes.fileName.overview')
              .pipe(take(1))
              .subscribe((fileName) => {
                var a = document.createElement('a');
                a.setAttribute('download', fileName + '.' + selectExport);
                a.href = URL.createObjectURL(data);
                a.click();
              });
          },
          () => {
            this.translation
              .translate('deliveryNotes.errorExportOrder')
              .pipe(take(1))
              .subscribe((message) => {
                this.messageService.add(
                  message,
                  GlobalMessageType.MSG_TYPE_ERROR
                );
              });
          }
        )
      )
      .subscribe((res) => { });
  }

  downLoadDeliveryNotesOverview(
    selectExport: string,
    formData: DeliveryNotesOverviewRequest,
    marketplace: boolean): Observable<Blob> {
    const queryParamters = {
      'selectExport': selectExport,
      'deliveryId': formData.deliveryId,
      'productCode': formData.productCode,
      'deliveryDateFrom': (formData.deliveryDateFrom?.length > 0) ? this.datepipe.transform(new Date(formData.deliveryDateFrom), 'dd.MM.yyyy') : '',
      'deliveryDateTo': (formData.deliveryDateTo?.length > 0) ? this.datepipe.transform(new Date(formData.deliveryDateTo), 'dd.MM.yyyy') : '',
      'isMarketplace': !!marketplace
    };

    const url = this.endpoints.buildUrl('deliveryNotesDownload', { queryParams: queryParamters });
    return this.httpClient.post(url, null, { observe: 'body', responseType: 'blob' })
  }

  getPdf(pdfUrl: string) {
    const params = {
      url: pdfUrl,
    };
    const url = this.endpoints.buildUrl('deliveryNotesPdf', { queryParams: params });
    this.httpClient
      .get(url, { observe: 'body', responseType: 'arraybuffer' })
      .pipe(
        tap(
          (data) => {
            var file = new Blob([data], { type: 'application/pdf' });
            var fileURL = URL.createObjectURL(file);
            window.open(fileURL);
          },
          () => {
            this.translation
              .translate('deliveryNotes.errorExportPdf')
              .pipe(take(1))
              .subscribe((message) => {
                this.messageService.add(
                  message,
                  GlobalMessageType.MSG_TYPE_ERROR
                );
              });
          }
        )
      )
      .subscribe((res) => { });
  }
}
